import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { finalize } from 'rxjs';
// Deep imports (not the domains/subscriber barrel) keep the layout shell import graph cycle-free.
import { RegistrationStatus } from '../../../domains/subscriber/registration-status/registration-status.enum';
import { SubscriberSearchResponseDto } from '../../../domains/subscriber/subscriber.model';
import { SubscriberService } from '../../../domains/subscriber/subscriber.service';
import { LayoutService } from '../../layout.service';

// Responsive sizes (DESIGN-SYSTEM.md §12.1): flexible up to 320px at FULL,
// icon+caret only at NARROW.
export type SubscriberChipSize = 'full' | 'icon';

/**
 * The top bar's compact global-subscriber context chip. This is intentionally separate from the
 * reusable SubscriberSelectComponent form field: the chip's PrimeNG content templates and
 * presentation-mode lock behavior are top-bar-only and must not leak into form-field consumers.
 */
@Component({
    selector: 'app-subscriber-chip-select',
    imports: [FormsModule, Select],
    templateUrl: './subscriber-chip-select.component.html',
    styleUrl: './subscriber-chip-select.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriberChipSelectComponent {
    private destroyRef = inject(DestroyRef);
    private layoutService = inject(LayoutService);
    private subscriberService = inject(SubscriberService);

    subscriberKey = model<number | null>(null);

    placeHolder = input<string | null>('Select subscriber');

    chipSize = input<SubscriberChipSize>('full');

    options = signal<SubscriberSearchResponseDto[]>([]);
    isLoading = signal<boolean>(false);

    isPresentationMode = this.layoutService.isPresentationMode;

    // Presentation mode renders the chip as a static, full-strength element with a lock icon instead of
    // a disabled p-select — PrimeNG's disabled styling grays the name, but staff must still read whose
    // data is on screen, and no dropdown may open (no tenant-list leak).
    isLocked = computed(() => this.isPresentationMode());

    selectedSubscriber = computed(() => this.options().find(s => s.subscriberKey === this.subscriberKey()) ?? null);

    effectivePlaceholder = computed(() =>
        (this.isLoading() ? 'Loading subscribers...' : this.placeHolder()) ?? 'Select a subscriber');

    isIconSize = computed(() => this.chipSize() === 'icon');

    chipStyleClass = computed(() => `subscriber-chip subscriber-chip--${this.chipSize()}`);

    // Icon-only chips have no visible label, so the full name (or placeholder) must live on the root
    // as a tooltip and accessible name.
    iconChipLabel = computed(() =>
        this.isIconSize() ? (this.selectedSubscriber()?.subscriberName ?? this.effectivePlaceholder()) : null);

    onClearSubscriberClicked(select: Select) {
        this.subscriberKey.set(null);
        select.hide();
    }

    constructor() {
        /* Single fetch on init — the chip always lists Live, non-internal subscribers. */
        this.isLoading.set(true);
        this.subscriberService
            .search([], [RegistrationStatus.Live], false, [], '')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe(subscribers => this.options.set(subscribers));
    }
}
